import { ColumnPreferencesProvider } from "@context-providers/table/column-preferences-provider";
import { MembersTable } from "@components/table/members/members-table";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { BaseGroupDetailsProps } from "@custom-types/group-types";
import { MembersSortBy } from "@custom-types/member-types";
import { fetchingGroupsFlagsSelector } from "@store/groups/groups-selector";
import { useAppSelector } from "@store/store-helper";
import { MembersTableColumnField } from "@components/table/members/members-table-types";
import { MemberBulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { sortMembers } from "@utils/member-utils";

interface Props extends Partial<BaseGroupDetailsProps>, BaseCompanyIdProps {
  /** Flag whether the content should be shown as skeletons because it is still loading */
  isLoading?: boolean;

  /** List of all the bulk actions and callbacks */
  bulkActionButtons: MemberBulkButtonComponents;

  /** Current sort by value from the sort dropdown */
  sortBy: MembersSortBy;

  /** Callback to update the sort by value in the sort dropdown */
  setSortBy: (sortBy: MembersSortBy) => void;
}

/** Columns for group members table */
export const GROUP_MEMBERS_COLUMNS: MembersTableColumnField[] = [
  "name",
  "email",
  "status",
  "role",
  "joinedDate",
  "options",
];

/**
 * Contains the Managers table of the group details
 */
export function GroupManagersTable({
  group,
  companyId,
  isLoading = false,
  bulkActionButtons,
  sortBy,
  setSortBy,
}: Props): JSX.Element {
  const { isFetchingGroups } = useAppSelector(fetchingGroupsFlagsSelector);

  const sortedMembers = sortMembers(group?.members ?? [], sortBy);

  return (
    <ColumnPreferencesProvider
      tableKey="group-members-table"
      columns={GROUP_MEMBERS_COLUMNS}
      options={{
        nonConfigurableColumns: ["options"],
        defaultVisibleColumns: [
          "name",
          "email",
          "status",
          "role",
          "joinedDate",
        ],
      }}
    >
      <MembersTable
        companyId={companyId}
        members={sortedMembers}
        tableSubject={"group"}
        isLoading={isFetchingGroups || isLoading}
        group={group}
        bulkActionButtons={bulkActionButtons}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
    </ColumnPreferencesProvider>
  );
}
